import { useState } from 'react';
import { t } from 'i18next';

import PageLayout from '@/components/Layout/PageLayout';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { selectMeasurementSource, useGetCageDailyLice } from '@/domain/lice';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { dayToString, toDateString } from '@/utils/date';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';

import CageLiceFemaleLicePageContent from './CageLiceFemaleLicePageContent';

export default function CageLiceFemaleLiceContainer({ cageId }: CagePageInterface) {
    const { client, stock } = useCage();
    const cageName = useCageName();
    const { isOptoScaleAdmin } = usePermissions();
    const { from, to, daypartTag, setFilter, selectedFishGroupId } = useCageFilter();

    const defaultMeasurmentSource = client?.featureFlags?.includes('LICE_LEGACY_VIEW')
        ? 'raw'
        : 'legacy';

    const isChileanLice = isChileanLiceType(stock?.liceType);
    const showToggle = isOptoScaleAdmin ? true : !isChileanLice;
    const pageTitle = isChileanLice
        ? 'common.labels.ovigerousFemale'
        : 'cageLice.common.matureFemaleLice';
    const [measurementSource, setMeasurementSource] = useState<'raw' | 'legacy'>(
        defaultMeasurmentSource
    );

    const dayFrom = from ? dayToString(from) : undefined;
    const dayTo = to ? dayToString(to) : undefined;

    const dailyLice = useGetCageDailyLice(
        cageId,
        { dayFrom, dayTo, dayPart: daypartTag, fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );

    const handleSetCageFilter = (dateReturn: DateRange) => {
        setFilter({
            from: dateReturn.dateFrom,
            to: dateReturn.dateTo,
        });
    };

    return (
        <PageLayout
            title={cageName}
            subtitle={t(pageTitle)}
            actions={
                <div>
                    <div>
                        {showToggle && (
                            <SegmentedControl<'raw' | 'legacy'>
                                defaultValue={measurementSource}
                                onChange={(value) => setMeasurementSource(value)}
                                options={[
                                    {
                                        value: 'raw',
                                        label: t('common.labels.rawData'),
                                        tooltip: t('common.labels.rawdataexplain'),
                                    },
                                    {
                                        value: 'legacy',
                                        label: t('common.labels.simulatedManualCount'),
                                        tooltip: t('common.labels.reportdataexplain'),
                                    },
                                ]}
                            />
                        )}
                    </div>
                </div>
            }>
            <CageLiceFemaleLicePageContent
                liceCount={dailyLice.data.map((row) =>
                    selectMeasurementSource(row, measurementSource)
                )}
                isLoading={dailyLice.isLoading}
                error={dailyLice.error}
                retry={retryFailedQueries(dailyLice)}
                cageFilterHandler={handleSetCageFilter}
                inputDateObject={{
                    dateFrom: toDateString(from),
                    dateTo: toDateString(to),
                }}
                isChileanLice={isChileanLice}
            />
        </PageLayout>
    );
}
